import { defineStore } from "pinia";
import { ref } from "vue";
import { useCookie, useNuxtApp, refreshCookie } from "#app";
import { useWidgetStore } from "~/store/widget";
import { useCompanyStore } from "~/store/company";
import { useItemStore } from "~/store/item";
import type { AuthUser, AuthUserCompany, EnableMfaResponse, Organisation, PermissionResponse } from "~~/api/models";
import { piniaPluginPersistedstate } from "#imports";
import { useConfigurationStore } from "~/store/configuration";
import type { CachedUserPermissions } from "~/components/documents/utils";
import { useCompanyFilterStore } from '~/store/companyFilter';
import { useSocketConnectionStore } from '~/store/socketConnection';

export const useUserStore = defineStore('user', () => {
  // State
  const darkMode = ref(false);
  const user = ref<AuthUser>({} as AuthUser);
  const userLanguage = ref('en');
  const userIsFetched = ref(false);
  const welcomeBackActive = ref<boolean>(true);
  const isAuthenticated = ref(false);
  const pendingRememberMe = ref(false);
  const pendingMfaEmail = ref('');
  const allUserCompanies = ref<AuthUserCompany[]>([]);
  const userCompanies = ref<AuthUserCompany[]>([]);
  const allSerialNumbers = ref<(string | null | undefined)[]>([]);
  const serialNumbers = ref<(string | null | undefined)[]>([]);
  const activeOrganisation = ref<string | null>(null);
  const organisations = ref<Organisation[]>([]);
  const cachedPermissions = ref<CachedUserPermissions>({});
  const allowedPermissions = ref<PermissionResponse[]>([]);

  // Composable
  const nuxtApp = useNuxtApp();
  const widgetStore = useWidgetStore();
  const companyStore = useCompanyStore();
  const itemStore = useItemStore();
  const configurationStore = useConfigurationStore();
  const companyFilterStore = useCompanyFilterStore();
  const socketConnectionStore = useSocketConnectionStore();
  const persistedUserCookie = useCookie('user');

  // Actions
  const setDarkMode = (value: boolean) => {
    darkMode.value = value;
  };

  const setUser = async (newUser: AuthUser, setWidget: boolean = true) => {
    if (!user.value || user.value.id !== newUser.id)
      welcomeBackActive.value = await userIsOutOfOffice(newUser);
    user.value = newUser;
    userLanguage.value = newUser.language?.toLowerCase() || 'en';
    userIsFetched.value = true;
    isAuthenticated.value = true;
    activeOrganisation.value = newUser.activeOrganisation;
    allUserCompanies.value = newUser.companies || [];
    userCompanies.value = newUser.companies?.filter(company => company.companyOrganisationId === activeOrganisation.value) || [];
    allSerialNumbers.value = newUser.companies?.map(company => company.companySerialNumber) || [];
    // Serial numbers should be all company serial numbers where the organisationId is the same as the active organisation
    serialNumbers.value = newUser.companies?.filter(company => company.companyOrganisationId === activeOrganisation.value)?.map(company => company.companySerialNumber) || [];
    organisations.value = newUser.organisations || [];

    await companyFilterStore.loadForActiveOrganisation();

    // If the widgetStore.widgets array is empty, we should populate it
    if (widgetStore.widgets.length === 0 && setWidget) {
      await nuxtApp.runWithContext(() => widgetStore.getWidgets());
    }
  };
  const userIsOutOfOffice = async (user: AuthUser) => {
    try {
      const $dayjs  = nuxtApp.$dayjs;
      return !!(user && user.workStatus && user.workStatus.startDate && $dayjs(user.workStatus.startDate).isSameOrBefore($dayjs()) && (!user.workStatus.endDate || $dayjs(user.workStatus.endDate).isSameOrAfter($dayjs())));
    } catch (error) {
      console.error('userIsOutOfOffice error', error);
      return false;
    }

  }

  const resetUser = () => {
    resetStore();
    persistedUserCookie.value = null;
  };

  const login = async (email: string, password: string, rememberMe = false) => {
    pendingRememberMe.value = rememberMe;
    const resolution = `${window.innerWidth}x${window.innerHeight}`;
    const response = await nuxtApp.$apiService.User.login(email, password, {'x-resolution': resolution}, rememberMe);
    clearLastViewedDocumentItem();
    if (response.success) {
      if (response.twoFactorRequired) {
        pendingMfaEmail.value = email;
        isAuthenticated.value = false;
        userIsFetched.value = false;
      } else {
        refreshCookie('token_expires');
        await setUser(response.data);
        pendingRememberMe.value = false;
        pendingMfaEmail.value = '';
      }
      return {success: true, user: response.data, twoFactorRequired: response.twoFactorRequired};
    }
    return {success: false, message: response.message};
  };

  const loginmfa = async (email: string, mfacode: string) => {
    const resolution = `${window.innerWidth}x${window.innerHeight}`;
    const response = await nuxtApp.$apiService.User.loginmfa(email, mfacode, {'x-resolution': resolution}, pendingRememberMe.value);
    clearLastViewedDocumentItem();
    if (response.success && !response.twoFactorRequired) {
      refreshCookie('token_expires');
      await setUser(response.data);
      pendingRememberMe.value = false;
      pendingMfaEmail.value = '';
      return {success: true, user: response.data, twoFactorRequired: response.twoFactorRequired};
    }
    return {success: false, message: response.message};
  };

  const logout = async () => {
    // An offline request cannot remove an HttpOnly cookie; do not pretend logout succeeded.
    await nuxtApp.$apiService.User.logout();
    refreshCookie('token_expires');
    try {
      if (import.meta.client) await socketConnectionStore.closeConnection();
    } catch {
      console.warn('Unable to close the notification connection after logout.');
    } finally {
      widgetStore.cleanWidgetStore();
      companyStore.cleanCompanyStore();
      itemStore.cleanItemStore();
      configurationStore.cleanConfigurationStore();
      companyFilterStore.reset();
      clearLastViewedDocumentItem();
      resetUser();
    }

    return true;
  };

  const changeLanguage = async (language: string) => {
    return await nuxtApp.$apiService.User.changeLanguage(language);
  };

  const forgotPassword = async (email: string, returnLink: string) => {
    return await nuxtApp.$apiService.User.forgotPassword(email, returnLink);
  };

  const validateResetToken = async (email: string, token: string) => {
    return await nuxtApp.$apiService.User.validateResetToken(email, token);
  };

  const resetPassword = async (email: string, password: string, confirmpassword: string, token: string) => {
    return await nuxtApp.$apiService.User.resetPassword(email, password, confirmpassword, token);
  };

  const enableTwoFactorAuth = async (): Promise<EnableMfaResponse> => {
    return await nuxtApp.$apiService.User.enableTwoFactorAuth();
  };

  const activateTwoFactorAuth = async (mfaInfo: EnableMfaResponse): Promise<EnableMfaResponse> => {
    const response = await nuxtApp.$apiService.User.activateTwoFactorAuth(mfaInfo);

    if (response.errorMessage!.length <= 0)
      user.value.twoFactorEnabled = true;

    return response;
  };

  const refreshToken = async (organisationId: string | null = null) => {
    const response = await nuxtApp.$apiService.User.refreshUserToken(organisationId);

    if (response?.success) {
      refreshCookie('token_expires');
      await setUser(response.data);
    }
  };

  const fetchUser = async () => {
    let response: AuthUser | null = null;

    response = await nuxtApp.$apiService.User.fetchUser();

    if (response) {
      await setUser(response);
    } else {
      resetUser();
    }
  };

  const getActiveOrganisation = (): Organisation | null | undefined => {
    // go through the organisations and find the activeOrganisation id
    return organisations.value.find(organisation => organisation.id === activeOrganisation.value);
  };

  const validateActiveOrganisation = async (tenantId: string) => {
    // If the logged-in user doesn't have more than one organisation, we can skip this validation
    if (organisations.value.length <= 1) return;

    const associatedCompany = allUserCompanies.value.find(company => company.companySerialNumber === tenantId);
    if (!associatedCompany) throw new Error('No company associated with the active organisation');

    const associatedOrganisation = organisations.value.find(organisation => organisation.id === associatedCompany.companyOrganisationId);
    if (!associatedOrganisation) throw new Error('No organisation associated with the active organisation');

    // If the active organisation is not the same as the one we're currently working in, refresh the token for the associated organisation
    if (associatedOrganisation.id !== activeOrganisation.value)
      await refreshToken(associatedOrganisation.id);
  };

  const getPermission = async (company: string, permission: string): Promise<PermissionResponse | null> => {
    const cachedPermissions = getCachedPermissions(company);

    if (cachedPermissions.length > 0) {
      const foundPermission = cachedPermissions.find((x: PermissionResponse) => x.key === permission);
      if (foundPermission) return foundPermission;
    }
    try {
      nuxtApp.$apiService.activeTenant = company;
      const userPermission: PermissionResponse = await nuxtApp.$apiService.User.getUserPermission(permission) as PermissionResponse;
      setCachedPermission(company, userPermission);
      return userPermission;
    } catch (error) {
      console.error(`Failed to fetch permission ${permission} for company ${company}:`, error);
      return null;
    }
  };

  const getPermissions = async (company: string, permissions: string[]): Promise<PermissionResponse[] | null> => {
    let cachedPermissions = getCachedPermissions(company);

    // Find permissions already loaded that match the requested keys.
    let foundPermissions = cachedPermissions.filter((x: PermissionResponse) =>
      permissions.includes(x.key)
    );

    // Determine which permission keys are missing.
    const missingPermissionKeys = permissions.filter((x: string) => !cachedPermissions.some((y: PermissionResponse) => y.key === x));

    // If any permissions are missing, fetch them.
    if (missingPermissionKeys.length > 0) {
      // If your API supports batch requests, you might use:
      nuxtApp.$apiService.activeTenant = company;
      const fetchedPermissions = await nuxtApp.$apiService.User.getUserPermissions(missingPermissionKeys);
      setCachedPermissions(company, fetchedPermissions);
      // re-fetch the cachedPermissions
      cachedPermissions = getCachedPermissions(company);

      // Re-evaluate found permissions after fetching missing ones.
      foundPermissions = cachedPermissions.filter((x: PermissionResponse) =>
        permissions.includes(x.key)
      );
    }

    return foundPermissions;
  };

  const setCachedPermission = (company: string, permission: PermissionResponse) => {
    if (!cachedPermissions.value[company]) cachedPermissions.value[company] = [];
    cachedPermissions.value[company].push(permission);
  };

  const setCachedPermissions = (company: string, permissions: PermissionResponse[]) => {
    if (!cachedPermissions.value[company]) cachedPermissions.value[company] = [];
    cachedPermissions.value[company].push(...permissions);
  };

  const getCachedPermissions = (company: string) => {
    if (!cachedPermissions.value[company]) return [];

    return cachedPermissions.value[company];
  };

  const hasReadPermission = (company: string, permissionKey: string) => {
    const permission = getCachedPermissions(company).find(x => x.key.toUpperCase() === permissionKey.toUpperCase());
    return permission?.read;
  }

  const hasWritePermission = (company: string, permissionKey: string) => {
    const permission = getCachedPermissions(company).find(x => x.key.toUpperCase() === permissionKey.toUpperCase());
    // To make sure a permission has write-access, it also, per definition needs read-access.+
    return permission?.read && permission?.write;
  }

  const getAllowedPermissions = async (permissions: string[]): Promise<PermissionResponse[]> => {
    // Find permissions already loaded that match the requested keys.
    let foundPermissions = allowedPermissions.value.filter((x: PermissionResponse) =>
      permissions.includes(x.key)
    );

    // Determine which permission keys are missing.
    const missingPermissionKeys = permissions.filter(key =>
      !allowedPermissions.value.some((x: PermissionResponse) => x.key === key)
    );

    // If any permissions are missing, fetch them.
    if (missingPermissionKeys.length > 0) {
      // If your API supports batch requests, you might use:
      const fetchedPermissions = await nuxtApp.$apiService.User.getAllowedPermissions(missingPermissionKeys);
      allowedPermissions.value.push(...fetchedPermissions);

      // Re-evaluate found permissions after fetching missing ones.
      foundPermissions = allowedPermissions.value.filter((x: PermissionResponse) =>
        permissions.includes(x.key)
      );
    }

    return foundPermissions;
  };

  const getAllowedPermission = (permission: string): PermissionResponse | undefined | null => {
    return allowedPermissions.value.find(x => x.key.toUpperCase() === permission.toUpperCase());
  }

  const hasAllowedReadPermission = (permissionKey: string) => {
    const permission = getAllowedPermission(permissionKey);
    return permission?.read;
  }

  const hasAllowedWritePermission = (permissionKey: string) => {
    const permission = getAllowedPermission(permissionKey);
    return permission?.write;
  }

  // Reset store
  const resetStore = () => {
    darkMode.value = false;
    user.value = {} as AuthUser;
    userLanguage.value = 'en';
    userIsFetched.value = false;
    isAuthenticated.value = false;
    pendingRememberMe.value = false;
    pendingMfaEmail.value = '';
    activeOrganisation.value = null;
    organisations.value = [];
    userCompanies.value = [];
    allUserCompanies.value = [];
    serialNumbers.value = [];
    allSerialNumbers.value = [];
    cachedPermissions.value = {};
    allowedPermissions.value = [];
    welcomeBackActive.value = true;
  };

  return {
    darkMode,
    user,
    userLanguage,
    userIsFetched,
    isAuthenticated,
    pendingRememberMe,
    pendingMfaEmail,
    userCompanies,
    serialNumbers,
    allSerialNumbers,
    organisations,
    activeOrganisation,
    allUserCompanies,
    cachedPermissions,
    welcomeBackActive,
    setDarkMode,
    setUser,
    resetUser,
    login,
    loginmfa,
    logout,
    changeLanguage,
    forgotPassword,
    validateResetToken,
    resetPassword,
    enableTwoFactorAuth,
    activateTwoFactorAuth,
    refreshToken,
    fetchUser,
    getActiveOrganisation,
    validateActiveOrganisation,
    getPermission,
    getPermissions,
    hasReadPermission,
    hasWritePermission,
    getAllowedPermissions,
    getAllowedPermission,
    hasAllowedReadPermission,
    hasAllowedWritePermission
  };
}, {
  persist: [{
    pick: ['darkMode', 'userLanguage', 'welcomeBackActive'],
    storage: piniaPluginPersistedstate.cookies({sameSite: 'lax'})
  }, {
    pick: ['pendingRememberMe', 'pendingMfaEmail'],
    storage: piniaPluginPersistedstate.sessionStorage()
  }]
});